import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n/i18n";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Set", "Oct", "Nov", "Dic"];
const COLORES = ["#14b8a6", "#2563eb", "#f97316", "#7c3aed", "#e11d48", "#0f172a"];
const usd = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const corto = (n) => (n == null ? "" : `$${Math.round(n / 1000)}k`);

export const MonthlyIncomeChart = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [anio, setAnio] = useState(null);          // null = comparar todos
  const [editando, setEditando] = useState(null);  // "2025-3"
  const [valor, setValor] = useState("");

  const cargar = () => api.get("/stats/monthly-income").then((r) => {
    setData(r.data);
    setAnio((prev) => (prev === undefined ? null : prev ?? Number(r.data.current_month.slice(0, 4))));
  }).catch(() => setData(null));

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const anios = useMemo(() => (data?.years || []).map((y) => y.year), [data]);
  const actual = Number(data?.current_month?.slice(0, 4));

  const filas = useMemo(() => {
    if (!data) return [];
    return MESES.map((m, i) => {
      const fila = { mes: m };
      for (const y of data.years) {
        const celda = y.months[i];
        fila[`a${y.year}`] = celda.income ?? null;
        if (y.year === actual) fila.proyeccion = celda.projection ?? null;
      }
      return fila;
    });
  }, [data, actual]);

  const guardar = async (year, month, campo = "income") => {
    const limpio = valor.replace(/[^\d.-]/g, "");
    try {
      await api.put("/stats/monthly-income", {
        year, month, [campo]: limpio === "" ? null : Number(limpio),
      });
      setEditando(null);
      setValor("");
      toast.success(limpio === "" ? t("income_cleared") : t("income_saved"));
      cargar();
    } catch {
      toast.error(t("income_save_error"));
    }
  };

  if (!data) return null;

  const mostrados = anio ? [anio] : anios;
  const anteriorDe = anio && anios.includes(anio - 1) ? anio - 1 : null;

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5" data-testid="monthly-income-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-semibold">{t("income_title")}</h3>
          <p className="mt-0.5 text-[11.5px] text-slate-400">
            {t("income_subtitle")}
          </p>
        </div>
        <div className="flex gap-1.5">
          {anios.map((y) => (
            <button
              key={y}
              data-testid={`income-year-${y}`}
              onClick={() => setAnio(y)}
              className={`text-[11px] font-bold px-2.5 py-1.5 rounded-full border transition-colors ${
                anio === y ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
            >{y}</button>
          ))}
          <button
            data-testid="income-year-all"
            onClick={() => setAnio(null)}
            className={`text-[11px] font-bold px-2.5 py-1.5 rounded-full border transition-colors ${
              anio === null ? "bg-slate-900 border-slate-900 text-white"
                : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
          >{t("income_compare_all")}</button>
        </div>
      </div>

      <div className="mt-4 h-[300px]" data-testid="income-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filas} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={corto} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={48} />
            <Tooltip
              formatter={(v, name) => [usd(v), name]}
              contentStyle={{ borderRadius: 12, border: "1px solid #e9edf2", fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 11.5 }} />
            {anio ? (
              <Bar dataKey={`a${anio}`} name={`Ingresos ${anio}`} fill="#14b8a6" radius={[5, 5, 0, 0]} maxBarSize={38} />
            ) : (
              mostrados.map((y, i) => (
                <Line key={y} type="monotone" dataKey={`a${y}`} name={`${y}`} dot={false}
                      stroke={COLORES[i % COLORES.length]} strokeWidth={2.2} connectNulls={false} />
              ))
            )}
            {anteriorDe && (
              <Line type="monotone" dataKey={`a${anteriorDe}`} name={`Ingresos ${anteriorDe}`} dot={false}
                    stroke="#cbd5e1" strokeWidth={2} strokeDasharray="5 5" connectNulls={false} />
            )}
            {anio === actual && (
              <Line type="monotone" dataKey="proyeccion" name={t("income_projection")} stroke="#0f172a"
                    strokeWidth={2.2} dot={{ r: 3, fill: "#fff", strokeWidth: 2 }} connectNulls />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.1em] text-slate-400 border-b border-slate-100">
              <th className="text-left font-medium px-2 py-2">{t("income_year")}</th>
              {MESES.map((m) => <th key={m} className="text-right font-medium px-2 py-2">{m}</th>)}
              <th className="text-right font-medium px-2 py-2">{t("income_total")}</th>
            </tr>
          </thead>
          <tbody>
            {data.years.map((y) => (
              <tr key={y.year} className="border-t border-slate-50" data-testid={`income-row-${y.year}`}>
                <td className="px-2 py-1.5 font-semibold whitespace-nowrap">
                  {y.year}
                  {y.year === actual && (
                    <span className="ml-1 text-[9.5px] font-extrabold text-teal-600 uppercase">· auto</span>
                  )}
                </td>
                {y.months.map((c) => {
                  const clave = `inc-${y.year}-${c.month}`;
                  const enEdicion = editando === clave;
                  return (
                    <td key={c.month}
                        className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap cursor-text
                                    ${c.source === "auto" ? "text-teal-700" : "text-slate-700"}
                                    ${enEdicion ? "" : "hover:bg-slate-50"}`}
                        data-testid={`income-cell-${y.year}-${c.month}`}
                        onClick={() => {
                          if (enEdicion) return;
                          setEditando(clave);
                          setValor(c.income != null ? String(c.income) : "");
                        }}>
                      {enEdicion ? (
                        <input
                          autoFocus
                          data-testid={`income-input-${y.year}-${c.month}`}
                          value={valor}
                          onChange={(e) => setValor(e.target.value)}
                          onBlur={() => guardar(y.year, c.month)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") guardar(y.year, c.month);
                            if (e.key === "Escape") { setEditando(null); setValor(""); }
                          }}
                          className="w-[86px] text-right border border-slate-300 rounded px-1 py-0.5 text-[12px]"
                        />
                      ) : (
                        c.income == null ? <span className="text-slate-300">—</span>
                          : Number(c.income).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right font-bold tabular-nums whitespace-nowrap">
                  {y.total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
            {(() => {
              const añoActual = data.years.find((y) => y.year === actual);
              if (!añoActual) return null;
              return (
                <tr className="border-t border-slate-100 bg-slate-50/40" data-testid="income-row-projection">
                  <td className="px-2 py-1.5 font-semibold whitespace-nowrap text-slate-600">
                    {t("income_projection")}
                  </td>
                  {añoActual.months.map((c) => {
                    const clave = `proj-${actual}-${c.month}`;
                    const enEdicion = editando === clave;
                    return (
                      <td key={c.month}
                          className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap cursor-text text-slate-600 ${enEdicion ? "" : "hover:bg-slate-100"}`}
                          data-testid={`projection-cell-${actual}-${c.month}`}
                          onClick={() => {
                            if (enEdicion) return;
                            setEditando(clave);
                            setValor(c.projection != null ? String(c.projection) : "");
                          }}>
                        {enEdicion ? (
                          <input
                            autoFocus
                            data-testid={`projection-input-${actual}-${c.month}`}
                            value={valor}
                            onChange={(e) => setValor(e.target.value)}
                            onBlur={() => guardar(actual, c.month, "projection")}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") guardar(actual, c.month, "projection");
                              if (e.key === "Escape") { setEditando(null); setValor(""); }
                            }}
                            className="w-[86px] text-right border border-slate-300 rounded px-1 py-0.5 text-[12px]"
                          />
                        ) : (
                          c.projection == null ? <span className="text-slate-300">—</span>
                            : Number(c.projection).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right font-bold tabular-nums whitespace-nowrap text-slate-600">
                    {añoActual.months.reduce((a, c) => a + (c.projection || 0), 0)
                      .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              );
            })()}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50/70 font-bold" data-testid="income-row-total">
              <td className="px-2 py-2">{t("income_total")}</td>
              {MESES.map((m, i) => {
                const suma = data.years.reduce((acc, y) => acc + (y.months[i].income || 0), 0);
                return (
                  <td key={m} className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                    {suma ? suma.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
                  </td>
                );
              })}
              <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                {data.years.reduce((a, y) => a + y.total, 0)
                  .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-3 text-[11.5px] text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-3.5 py-2.5">
        {t("income_help")}
      </p>
    </div>
  );
};

export default MonthlyIncomeChart;
